import React, { useEffect, useMemo, useState } from 'react';
import { TaskStatus, TaskStatusLabels, TaskStatusColors } from '../../types';
import { CheckCircle2, ChevronDown } from 'lucide-react';

interface TaskStatusSelectorProps {
  currentStatus: TaskStatus;
  validTransitions: TaskStatus[];
  onStatusChange: (status: TaskStatus) => void;
}

export const TaskStatusSelector: React.FC<TaskStatusSelectorProps> = ({
  currentStatus,
  validTransitions,
  onStatusChange,
}) => {
  const currentColor = TaskStatusColors[currentStatus];
  const [selectedStatus, setSelectedStatus] = useState('');

  const transitionOptions = useMemo(() => (
    validTransitions.map(next => ({
      value: next,
      label: TaskStatusLabels[next],
      helper:
        next === 'STARTED' ? 'Start task' :
        next === 'IN_PROGRESS' ? (currentStatus === 'IN_REVIEW' ? 'Return to the technician for rework' : 'Resume or continue work') :
        next === 'IN_REVIEW' ? 'Send for supervisor review' :
        next === 'PAUSED' ? 'Pause current work' :
        next === 'FINISHED' ? (currentStatus === 'IN_REVIEW' ? 'Approve and close the task' : 'Mark task as finished') :
        'Report or keep track of issues',
    }))
  ), [validTransitions, currentStatus]);

  useEffect(() => {
    setSelectedStatus('');
  }, [currentStatus, validTransitions]);

  return (
    <div className="detail-card full-width status-selector-card">
      <div className="status-selector-layout">
        <div className="status-selector-current">
          <span className="status-selector-label">Current Status</span>
          <span
            className="status-selector-badge"
            style={{
              background: `${currentColor}22`,
              color: currentColor,
              borderColor: `${currentColor}55`,
            }}
          >
            {TaskStatusLabels[currentStatus]}
          </span>
        </div>

        {validTransitions.length > 0 ? (
          <div className="status-selector-transitions">
            <span className="status-selector-label">Update Status</span>
            <div className="status-selector-select-wrap">
              <select
                className="status-selector-dropdown"
                value={selectedStatus}
                onChange={e => {
                  const nextStatus = e.target.value as TaskStatus;
                  setSelectedStatus(nextStatus);
                  if (!nextStatus) return;
                  onStatusChange(nextStatus);
                  setSelectedStatus('');
                }}
              >
                <option value="" disabled>Select next status...</option>
                {transitionOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <ChevronDown size={16} className="status-selector-dropdown-icon" />
            </div>
            <p className="status-selector-help">
              {transitionOptions.length === 1
                ? transitionOptions[0].helper
                : `${transitionOptions.length} valid transitions available for this task.`}
            </p>
          </div>
        ) : (
          <div className="status-selector-terminal">
            <span className="status-selector-label">Update Status</span>
            <span className="status-selector-terminal-message">
              <CheckCircle2 size={14} color={currentColor} />
              {currentStatus === 'IN_REVIEW'
                ? 'Waiting for supervisor review'
                : 'Task completed - no further transitions available'}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};
